import { Box, LinearProgress, Stack, Tooltip, Typography } from "@mui/material"
import {
  PlanAndNotePanel,
  SetTechniqueModal,
  ViraButton,
  ViraText,
  ViraTextField,
  type DayOfWeek,
  type InputStatusItem,
  type PlanAndNoteVariant,
  type PlanItem,
  type PlanItemSection,
  type TechniqueScheduleMode,
  type TheirWeekEntry,
  type NotifyScope,
} from "@ksana/vira-ui"
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react"
import { ClientFragment } from "../../generated/graphql"
import { useSession } from "../../hooks/useSession"
import {
  deriveScheduleHint,
  formatCadenceLabel,
  formatRecurrenceSummary,
  applyRecurrencePreset,
  defaultClinicianSchedule,
  recurrenceSelectOptions,
  resolveItemClinicianSchedule,
  serializeClinicianSchedule,
  theirWeekDaysForSchedule,
  type ClinicianSchedule,
  type ClinicianScheduleDay,
  type RecurrencePreset,
} from "../../utils/clinicianSchedule"
import {
  acceptCarePlanItem,
  CarePlanContingency,
  CarePlanItem,
  CarePlanResponse,
  fetchCarePlan,
  fetchProgressNote,
  fetchTranscriptSessionDetail,
  fetchTranscriptSessions,
  generateCarePlan,
  initializeManualCarePlan,
  patchCarePlan,
  patchCarePlanItem,
  rejectCarePlanItem,
  signCarePlan,
  TranscriptSessionDetail,
  type ProgressNoteStatus,
} from "../../utils/transcriptApi"

interface PostSessionCarePlanProps {
  client: ClientFragment
  practitionerId: string
  enabled: boolean
  /**
   * When true (`vira_loop`), use Figma Plan and note labels, sections,
   * Their week, honest inputs, and Loop schedule-delivery copy.
   */
  loopEnabled?: boolean
  /** Prefer this completed transcript session over “latest”. */
  preferredTranscriptSessionId?: string | null
  /** Scroll / focus target for “Draft the note” when Loop chrome is on. */
  onDraftNoteRequest?: () => void
  /**
   * When embedded in Past sessions → Progress Note, tighten spacing and
   * point empty-state CTAs at the current pane instead of “return here”.
   */
  embedded?: boolean
  /** Hide inline Session Status row (lifted above session tabs). */
  hideInputs?: boolean
  /** Report Session Status so parent can render it above tabs. */
  onInputsChange?: (status: {
    items: InputStatusItem[]
    readyCount: number
    totalCount: number
  }) => void
  /**
   * Live progress-note gate from the Progress note tab (draft / signed).
   * When omitted, Note shows as not drafted until a note status is reported.
   */
  progressNoteGate?: {
    status: "draft" | "signed" | null
    hasContent: boolean
  } | null
  /** Empty-state: record a recap into the preferred session. */
  onRequestRecordRecap?: (event: MouseEvent<HTMLElement>) => void
  /** Empty-state: upload a transcript into the preferred session. */
  onRequestUploadTranscript?: (file: File) => void
}

const COMPOSING_STATUSES = new Set(["composing", "signed"])
const LOCKED_STATUSES = new Set([
  "composing",
  "composed",
  "composition_failed",
  "signed",
])

export default function PostSessionCarePlan({
  client,
  practitionerId,
  enabled,
  loopEnabled = false,
  preferredTranscriptSessionId = null,
  onDraftNoteRequest,
  embedded = false,
  hideInputs = false,
  onInputsChange,
  progressNoteGate = null,
  onRequestRecordRecap,
  onRequestUploadTranscript,
}: PostSessionCarePlanProps) {
  const { session } = useSession()
  const uploadInputRef = useRef<HTMLInputElement | null>(null)

  const [transcriptSession, setTranscriptSession] =
    useState<TranscriptSessionDetail | null>(null)
  const [carePlan, setCarePlan] = useState<CarePlanResponse | null>(null)
  const [transcriptErrorMessage, setTranscriptErrorMessage] = useState<
    string | null
  >(null)
  const [actionErrorMessage, setActionErrorMessage] = useState<string | null>(
    null
  )
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false)
  const [isLoadingPlan, setIsLoadingPlan] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSigning, setIsSigning] = useState(false)
  const [busyItemId, setBusyItemId] = useState<string | null>(null)
  const [draftGoals, setDraftGoals] = useState<Record<string, string>>({})
  const [goalSaveErrors, setGoalSaveErrors] = useState<Record<string, string>>(
    {}
  )
  const [scheduleModalItemId, setScheduleModalItemId] = useState<string | null>(
    null
  )
  const [scheduleDraft, setScheduleDraft] = useState<ClinicianSchedule | null>(
    null
  )
  const [scheduleDraftTitle, setScheduleDraftTitle] = useState("")
  const [scheduleDraftGoal, setScheduleDraftGoal] = useState("")
  const [isSavingSchedule, setIsSavingSchedule] = useState(false)
  const [fetchedNoteGate, setFetchedNoteGate] = useState<{
    status: ProgressNoteStatus | null
    hasContent: boolean
  } | null>(null)

  const selectedClientName =
    client.first_name ||
    client.display_name ||
    client.full_name ||
    "this client"

  const planItems = useMemo(
    () => carePlan?.plan?.items ?? [],
    [carePlan?.plan?.items]
  )
  const planStatus = carePlan?.status ?? null
  const isLocked = planStatus ? LOCKED_STATUSES.has(planStatus) : false
  const approvedItems = useMemo(
    () =>
      planItems.filter((item) => {
        const decision = resolveItemDecision(item, carePlan?.approvedItems)
        return (
          decision === "approved" ||
          decision === "edited" ||
          decision === "signed"
        )
      }),
    [carePlan?.approvedItems, planItems]
  )
  const approvedCount = approvedItems.length
  const hasEmptyApprovedGoals = useMemo(
    () =>
      approvedItems.some((item) => {
        const goalText = (draftGoals[item.id] ?? item.goal ?? "").trim()
        return goalText.length === 0
      }),
    [approvedItems, draftGoals]
  )
  const pendingContingencyCount = useMemo(
    () =>
      approvedItems.reduce((count, item) => {
        return (
          count +
          item.contingencies.filter(
            (contingency) =>
              contingency.requiresApproval && !contingency.approved
          ).length
        )
      }, 0),
    [approvedItems]
  )

  const loadLatestTranscript = useCallback(async () => {
    if (!session?.jwt || !practitionerId) {
      return null
    }

    const sessions = await fetchTranscriptSessions(session.jwt, {
      clientId: client.id,
      practitionerId,
    })
    // Prefer the open session id even if list status is briefly stale.
    if (preferredTranscriptSessionId) {
      return fetchTranscriptSessionDetail(
        session.jwt,
        preferredTranscriptSessionId
      )
    }
    const latestCompleteSession = [...sessions]
      .filter((currentSession) => currentSession.status === "complete")
      .sort(
        (left, right) =>
          new Date(right.createdAt).getTime() -
          new Date(left.createdAt).getTime()
      )[0]

    if (!latestCompleteSession) {
      return null
    }

    return fetchTranscriptSessionDetail(session.jwt, latestCompleteSession.id)
  }, [client.id, practitionerId, preferredTranscriptSessionId, session?.jwt])

  useEffect(() => {
    if (!enabled || !session?.jwt || !practitionerId) {
      setTranscriptSession(null)
      setCarePlan(null)
      setTranscriptErrorMessage(null)
      setActionErrorMessage(null)
      setDraftGoals({})
      setGoalSaveErrors({})
      return
    }

    let ignore = false

    const bootstrap = async () => {
      try {
        setIsLoadingTranscript(true)
        setIsLoadingPlan(true)
        setTranscriptErrorMessage(null)
        setActionErrorMessage(null)

        const detail = await loadLatestTranscript()
        if (ignore) {
          return
        }

        if (!detail) {
          // Still bind preferred open session id so we never show the
          // "Nothing to work from yet" empty-hero when a session is open.
          if (preferredTranscriptSessionId) {
            try {
              const preferredDetail = await fetchTranscriptSessionDetail(
                session.jwt,
                preferredTranscriptSessionId
              )
              if (ignore) {
                return
              }
              setTranscriptSession(preferredDetail)
            } catch {
              if (!ignore) {
                setTranscriptSession(null)
                setCarePlan(null)
              }
              return
            }
          } else {
            setTranscriptSession(null)
            setCarePlan(null)
            if (!loopEnabled) {
              setTranscriptErrorMessage(
                "A completed transcript is required to generate a between-session care plan."
              )
            }
            return
          }
        } else {
          setTranscriptSession(detail)
        }
        setTranscriptErrorMessage(null)

        const sessionIdForPlan =
          detail?.session.id ?? preferredTranscriptSessionId
        if (!sessionIdForPlan) {
          setCarePlan(null)
          return
        }

        try {
          const existing = await fetchCarePlan(session.jwt, sessionIdForPlan)
          if (ignore) {
            return
          }
          if (existing.plan) {
            setCarePlan(existing)
            setDraftGoals(buildGoalDrafts(existing.plan.items ?? []))
          } else {
            // Manual-first: empty editable plan so clinicians can add nudges
            // without generating. Session Status stays "Not drafted" until items.
            const initialized = await initializeManualCarePlan(
              session.jwt,
              sessionIdForPlan
            )
            if (ignore) {
              return
            }
            setCarePlan(initialized)
            setDraftGoals(buildGoalDrafts(initialized.plan?.items ?? []))
          }
          setGoalSaveErrors({})
          setActionErrorMessage(null)
        } catch (planError) {
          if (ignore) {
            return
          }
          // Keep the open session bound — plan failure must not wipe tab chrome.
          setCarePlan(null)
          setDraftGoals({})
          setActionErrorMessage(
            getErrorMessage(planError, "Unable to start a manual action plan.")
          )
        }
      } catch (error) {
        if (!ignore) {
          // Prefer keeping the open session context over the empty-hero card.
          if (!preferredTranscriptSessionId) {
            setTranscriptSession(null)
          }
          setCarePlan(null)
          setTranscriptErrorMessage(
            getErrorMessage(
              error,
              "Unable to load the transcript needed for this care plan."
            )
          )
        }
      } finally {
        if (!ignore) {
          setIsLoadingTranscript(false)
          setIsLoadingPlan(false)
        }
      }
    }

    void bootstrap()

    return () => {
      ignore = true
    }
  }, [
    client.id,
    enabled,
    loadLatestTranscript,
    loopEnabled,
    practitionerId,
    preferredTranscriptSessionId,
    session?.jwt,
  ])

  useEffect(() => {
    if (!enabled || !session?.jwt || !transcriptSession?.session.id) {
      setFetchedNoteGate(null)
      return
    }
    let ignore = false
    const loadNote = async () => {
      try {
        const note = await fetchProgressNote(
          session.jwt,
          transcriptSession.session.id
        )
        if (ignore) {
          return
        }
        setFetchedNoteGate({
          status: note.status,
          hasContent: Boolean(note.soapText?.trim() || note.birpText?.trim()),
        })
      } catch {
        if (!ignore) {
          setFetchedNoteGate({ status: null, hasContent: false })
        }
      }
    }
    void loadNote()
    return () => {
      ignore = true
    }
  }, [enabled, session?.jwt, transcriptSession?.session.id])

  useEffect(() => {
    if (
      !enabled ||
      !session?.jwt ||
      !transcriptSession ||
      !planStatus ||
      !COMPOSING_STATUSES.has(planStatus)
    ) {
      return
    }

    let ignore = false
    const poll = async () => {
      try {
        const next = await fetchCarePlan(
          session.jwt,
          transcriptSession.session.id
        )
        if (!ignore) {
          setCarePlan(next)
          if (next.plan) {
            setDraftGoals(buildGoalDrafts(next.plan.items))
          }
        }
      } catch (error) {
        if (!ignore) {
          setActionErrorMessage(
            getErrorMessage(
              error,
              "Unable to refresh care plan composition status."
            )
          )
        }
      }
    }

    const intervalId = window.setInterval(() => {
      void poll()
    }, 1500)
    void poll()

    return () => {
      ignore = true
      window.clearInterval(intervalId)
    }
  }, [enabled, planStatus, session?.jwt, transcriptSession])

  const handleGenerate = async () => {
    if (!session?.jwt || !transcriptSession) {
      return
    }
    if (!transcriptDetailHasContent(transcriptSession)) {
      return
    }
    const hasExistingItems = Boolean(carePlan?.plan?.items?.length)
    if (
      hasExistingItems &&
      !window.confirm(
        "Auto-generating will replace the current action plan. Continue?"
      )
    ) {
      return
    }

    try {
      setIsGenerating(true)
      setActionErrorMessage(null)
      const generated = await generateCarePlan(
        session.jwt,
        transcriptSession.session.id
      )
      setCarePlan(generated)
      setDraftGoals(buildGoalDrafts(generated.plan?.items ?? []))
      setGoalSaveErrors({})
    } catch (error) {
      setActionErrorMessage(
        getErrorMessage(error, "Unable to generate the care plan draft.")
      )
    } finally {
      setIsGenerating(false)
    }
  }

  const handleAccept = async (itemId: string) => {
    if (!session?.jwt || !transcriptSession || isLocked) {
      return
    }
    try {
      setBusyItemId(itemId)
      setActionErrorMessage(null)
      const next = await acceptCarePlanItem(
        session.jwt,
        transcriptSession.session.id,
        itemId
      )
      setCarePlan(next)
    } catch (error) {
      setActionErrorMessage(
        getErrorMessage(error, "Unable to approve this plan item.")
      )
    } finally {
      setBusyItemId(null)
    }
  }

  const handleReject = async (itemId: string) => {
    if (!session?.jwt || !transcriptSession || isLocked) {
      return
    }
    try {
      setBusyItemId(itemId)
      setActionErrorMessage(null)
      const next = await rejectCarePlanItem(
        session.jwt,
        transcriptSession.session.id,
        itemId
      )
      setCarePlan(next)
    } catch (error) {
      setActionErrorMessage(
        getErrorMessage(error, "Unable to reject this plan item.")
      )
    } finally {
      setBusyItemId(null)
    }
  }

  const handleSaveGoal = async (item: CarePlanItem) => {
    if (!session?.jwt || !transcriptSession || isLocked) {
      return
    }
    const nextGoal = draftGoals[item.id] ?? item.goal ?? ""
    if (nextGoal === (item.goal ?? "")) {
      return
    }
    try {
      setBusyItemId(item.id)
      setGoalSaveErrors((current) => {
        const next = { ...current }
        delete next[item.id]
        return next
      })
      const next = await patchCarePlanItem(
        session.jwt,
        transcriptSession.session.id,
        item.id,
        { goal: nextGoal }
      )
      setCarePlan(next)
      setDraftGoals(buildGoalDrafts(next.plan?.items ?? []))
    } catch (error) {
      setGoalSaveErrors((current) => ({
        ...current,
        [item.id]: formatGoalSaveError(error),
      }))
    } finally {
      setBusyItemId(null)
    }
  }

  const handleContingencyDecision = async (
    item: CarePlanItem,
    contingencyId: string,
    approved: boolean
  ) => {
    if (!session?.jwt || !transcriptSession || isLocked || isSigning) {
      return
    }
    const current = item.contingencies.find(
      (contingency) => contingency.id === contingencyId
    )
    if (!current || current.approved === approved) {
      return
    }
    const contingencies = item.contingencies.map((contingency) =>
      contingency.id === contingencyId
        ? { ...contingency, approved }
        : contingency
    )
    const localGoal = draftGoals[item.id] ?? item.goal ?? ""
    const patchBody: {
      contingencies: CarePlanContingency[]
      goal?: string
    } = { contingencies }
    if (localGoal !== (item.goal ?? "")) {
      patchBody.goal = localGoal
    }
    try {
      setBusyItemId(item.id)
      setActionErrorMessage(null)
      const next = await patchCarePlanItem(
        session.jwt,
        transcriptSession.session.id,
        item.id,
        patchBody
      )
      setCarePlan(next)
      if (next.plan) {
        // Keep in-progress goal edits on other rows; a concurrent blur-save
        // must not be wiped by this whole-record response.
        setDraftGoals((currentDrafts) => {
          const fromServer = buildGoalDrafts(next.plan!.items)
          const merged = { ...fromServer }
          for (const planItem of next.plan!.items) {
            const local = currentDrafts[planItem.id]
            if (local !== undefined && local !== (planItem.goal ?? "")) {
              merged[planItem.id] = local
            }
          }
          return merged
        })
      }
    } catch (error) {
      setActionErrorMessage(
        getErrorMessage(error, "Unable to update this contingency.")
      )
    } finally {
      setBusyItemId(null)
    }
  }

  const handleSign = async () => {
    if (!session?.jwt || !transcriptSession || isLocked) {
      return
    }
    if (hasEmptyApprovedGoals) {
      setActionErrorMessage(
        "Add goal text to every approved item before signing."
      )
      return
    }
    try {
      setIsSigning(true)
      setActionErrorMessage(null)
      for (const item of approvedItems) {
        const nextGoal = (draftGoals[item.id] ?? item.goal ?? "").trim()
        if (nextGoal !== (item.goal ?? "").trim()) {
          await patchCarePlanItem(
            session.jwt,
            transcriptSession.session.id,
            item.id,
            { goal: nextGoal }
          )
        }
      }
      const next = await signCarePlan(session.jwt, transcriptSession.session.id)
      setCarePlan(next)
      setDraftGoals(buildGoalDrafts(next.plan?.items ?? []))
    } catch (error) {
      setActionErrorMessage(
        getErrorMessage(error, "Unable to sign the care plan.")
      )
    } finally {
      setIsSigning(false)
    }
  }

  const statusLabel = formatStatusLabel(planStatus, carePlan, loopEnabled)
  const visibleErrorMessage = transcriptErrorMessage || actionErrorMessage
  const showProgress =
    isLoadingTranscript || isLoadingPlan || isGenerating || isSigning

  const hasTranscriptContent = transcriptDetailHasContent(transcriptSession)
  const hasPlanItems = Boolean(carePlan?.plan?.items?.length)
  const hasPlanShell = Boolean(carePlan?.plan)
  // Prefer the open session id from the parent when embedded in a session tab.
  const boundSessionId =
    preferredTranscriptSessionId ?? transcriptSession?.session.id ?? null
  const canAutoGeneratePlan =
    Boolean(boundSessionId) &&
    hasTranscriptContent &&
    !isGenerating &&
    !showProgress &&
    !isLocked
  const generatePlanDisabledReason =
    showProgress || isGenerating
      ? "Please wait for the current action to finish."
      : !boundSessionId
      ? "Unable to bind this plan to the open session. Refresh and try again."
      : !hasTranscriptContent
      ? "Add a recording or transcript on the Transcript tab first, then auto-generate."
      : isLocked
      ? "This plan is signed and can no longer be regenerated."
      : ""

  useEffect(() => {
    if (
      !hasTranscriptContent &&
      actionErrorMessage ===
        "Add a recording or transcript on the Transcript tab first, then auto-generate."
    ) {
      setActionErrorMessage(null)
    }
  }, [actionErrorMessage, hasTranscriptContent])

  const panelVariant = resolvePanelVariant({
    hasSession: Boolean(transcriptSession || preferredTranscriptSessionId),
    planStatus,
    isGenerating: isGenerating || isLoadingPlan,
    hasPlan: hasPlanShell,
    itemCount: planItems.length,
  })

  const sessionDateForSchedule = useMemo(() => {
    const raw = transcriptSession?.session.createdAt || null
    if (!raw) {
      return null
    }
    const parsed = new Date(raw)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }, [transcriptSession?.session.createdAt])

  const NEW_PLAN_ITEM_ID = "__new__"

  const openScheduleModal = useCallback(
    (itemId: string) => {
      const item = planItems.find((candidate) => candidate.id === itemId)
      if (!item) {
        return
      }
      setScheduleModalItemId(itemId)
      setScheduleDraft(
        resolveItemClinicianSchedule(item, sessionDateForSchedule)
      )
      setScheduleDraftTitle(item.title || item.interventionId || item.id)
      setScheduleDraftGoal(draftGoals[item.id] ?? item.goal ?? "")
    },
    [draftGoals, planItems, sessionDateForSchedule]
  )

  const openCreateNudgeModal = useCallback(() => {
    setScheduleModalItemId(NEW_PLAN_ITEM_ID)
    setScheduleDraft(defaultClinicianSchedule(sessionDateForSchedule))
    setScheduleDraftTitle("")
    setScheduleDraftGoal("")
    setActionErrorMessage(null)
  }, [sessionDateForSchedule])

  const closeScheduleModal = useCallback(() => {
    setScheduleModalItemId(null)
    setScheduleDraft(null)
    setScheduleDraftTitle("")
    setScheduleDraftGoal("")
  }, [])

  const handleSaveSchedule = useCallback(async () => {
    const sessionId =
      transcriptSession?.session.id ?? preferredTranscriptSessionId ?? null
    if (!session?.jwt || !sessionId || !scheduleModalItemId || !scheduleDraft) {
      return
    }
    const nextGoal = scheduleDraftGoal.trim()
    const nextTitle = scheduleDraftTitle.trim() || "Between-session nudge"
    if (!nextGoal) {
      setGoalSaveErrors((current) => ({
        ...current,
        [scheduleModalItemId]: "Goal text is required",
      }))
      return
    }
    setIsSavingSchedule(true)
    setActionErrorMessage(null)
    try {
      if (scheduleModalItemId === NEW_PLAN_ITEM_ID) {
        let planRecord = carePlan
        if (!planRecord?.plan) {
          planRecord = await initializeManualCarePlan(session.jwt, sessionId)
        }
        const planRaw = {
          ...(planRecord.plan?.raw ?? {}),
          items: Array.isArray(planRecord.plan?.raw?.items)
            ? [...(planRecord.plan!.raw.items as unknown[])]
            : [],
        }
        const newId = `manual_${Date.now().toString(36)}`
        const newItem = {
          id: newId,
          title: nextTitle,
          goal: nextGoal,
          rationale: null,
          status: "approved",
          intervention_id: null,
          schedule_hint: deriveScheduleHint(scheduleDraft),
          parameters: {
            clinician_schedule: serializeClinicianSchedule(scheduleDraft),
          },
          personalization: {},
          contingencies: [],
        }
        planRaw.items = [...(planRaw.items as unknown[]), newItem]
        const next = await patchCarePlan(session.jwt, sessionId, {
          plan: planRaw,
          approvedItems: {
            ...(planRecord.approvedItems ?? {}),
            [newId]: "approved",
          },
        })
        setCarePlan(next)
        setDraftGoals(buildGoalDrafts(next.plan?.items ?? []))
        setGoalSaveErrors((current) => {
          const nextErrors = { ...current }
          delete nextErrors[NEW_PLAN_ITEM_ID]
          return nextErrors
        })
        closeScheduleModal()
        return
      }

      const item = planItems.find(
        (candidate) => candidate.id === scheduleModalItemId
      )
      if (!item) {
        return
      }
      const nextParameters = {
        ...item.parameters,
        clinician_schedule: serializeClinicianSchedule(scheduleDraft),
      }
      const next = await patchCarePlanItem(session.jwt, sessionId, item.id, {
        ...(nextTitle && nextTitle !== (item.title ?? "")
          ? { title: nextTitle }
          : {}),
        goal: nextGoal,
        parameters: nextParameters,
        scheduleHint: deriveScheduleHint(scheduleDraft),
      })
      setCarePlan(next)
      setDraftGoals(buildGoalDrafts(next.plan?.items ?? []))
      setGoalSaveErrors((current) => {
        const nextErrors = { ...current }
        delete nextErrors[item.id]
        return nextErrors
      })
      closeScheduleModal()
    } catch (error) {
      setActionErrorMessage(
        getErrorMessage(
          error,
          scheduleModalItemId === NEW_PLAN_ITEM_ID
            ? "Unable to add this nudge."
            : "Unable to save schedule for this item."
        )
      )
    } finally {
      setIsSavingSchedule(false)
    }
  }, [
    carePlan,
    closeScheduleModal,
    planItems,
    preferredTranscriptSessionId,
    scheduleDraft,
    scheduleDraftGoal,
    scheduleDraftTitle,
    scheduleModalItemId,
    session?.jwt,
    transcriptSession?.session.id,
  ])

  const panelPlanItems: PlanItem[] = useMemo(
    () =>
      planItems.map((item) => {
        const decision = resolveItemDecision(item, carePlan?.approvedItems)
        const goalValue = draftGoals[item.id] ?? item.goal ?? ""
        const schedule = resolveItemClinicianSchedule(
          item,
          sessionDateForSchedule
        )
        return {
          id: item.id,
          title: item.title || item.interventionId || item.id,
          detail: [goalValue.trim() || "Goal needed", item.rationale]
            .filter(Boolean)
            .join(" · "),
          cadence: formatCadenceLabel(schedule),
          approved:
            decision === "approved" ||
            decision === "edited" ||
            decision === "signed",
          rejected: decision === "rejected",
          disabled: isLocked || busyItemId === item.id,
          section: resolvePlanItemSection(item),
        }
      }),
    [
      busyItemId,
      carePlan?.approvedItems,
      draftGoals,
      isLocked,
      planItems,
      sessionDateForSchedule,
    ]
  )

  const loopPlanItems: PlanItem[] = loopEnabled
    ? panelPlanItems.map((panelItem) => {
        const item = planItems.find(
          (candidate) => candidate.id === panelItem.id
        )
        if (!item || panelItem.rejected || isLocked) {
          return panelItem
        }
        const isBusy = busyItemId === item.id
        const showContingencies =
          Boolean(panelItem.approved) && item.contingencies.length > 0
        if (!showContingencies) {
          return panelItem
        }
        return {
          ...panelItem,
          // Goal edits live in SetTechniqueModal; keep contingencies on the card.
          expandedContent: (
            <Stack spacing={1}>
              {item.contingencies.map((contingency) => (
                <ContingencyReviewRow
                  key={contingency.id}
                  contingency={contingency}
                  disabled={isBusy || isSigning}
                  onApprove={() => {
                    void handleContingencyDecision(item, contingency.id, true)
                  }}
                  onDecline={() => {
                    void handleContingencyDecision(item, contingency.id, false)
                  }}
                />
              ))}
            </Stack>
          ),
        }
      })
    : panelPlanItems

  const theirWeekEntries: TheirWeekEntry[] | undefined = useMemo(() => {
    if (!loopEnabled || !carePlan?.plan || planItems.length === 0) {
      return undefined
    }
    const entries: TheirWeekEntry[] = []
    for (const item of planItems) {
      const decision = resolveItemDecision(item, carePlan?.approvedItems)
      if (decision === "rejected") {
        continue
      }
      const approved =
        decision === "approved" ||
        decision === "edited" ||
        decision === "signed"
      const schedule = resolveItemClinicianSchedule(
        item,
        sessionDateForSchedule
      )
      const days = theirWeekDaysForSchedule(schedule)
      const label = truncateLabel(item.title || item.interventionId || item.id)
      if (days.length === 0) {
        continue
      }
      for (const day of days) {
        entries.push({
          id: `${item.id}-${day}`,
          day,
          label,
          proposed: !approved,
        })
      }
    }
    return entries
  }, [
    carePlan?.plan,
    carePlan?.approvedItems,
    loopEnabled,
    planItems,
    sessionDateForSchedule,
  ])

  const handleInitializeManualPlan = async () => {
    if (!session?.jwt || !transcriptSession) {
      return
    }
    try {
      setIsGenerating(true)
      setActionErrorMessage(null)
      const initialized = await initializeManualCarePlan(
        session.jwt,
        transcriptSession.session.id
      )
      setCarePlan(initialized)
      setDraftGoals(buildGoalDrafts(initialized.plan?.items ?? []))
      setGoalSaveErrors({})
    } catch (error) {
      setActionErrorMessage(
        getErrorMessage(error, "Unable to start a manual action plan.")
      )
    } finally {
      setIsGenerating(false)
    }
  }

  const inputs: InputStatusItem[] = useMemo(() => {
    const planReady =
      hasPlanItems &&
      planStatus !== null &&
      !["draft", "composing"].includes(planStatus)
    const planInProgress =
      isGenerating ||
      (hasPlanItems &&
        (planStatus === "draft" ||
          planStatus === "composing" ||
          (Boolean(carePlan?.plan) && !isLocked)))
    const planDetail =
      !hasPlanItems && !isGenerating ? "Not drafted" : statusLabel
    const appInput: InputStatusItem = loopEnabled
      ? {
          id: "app",
          title: "App data",
          status: "missing",
          detail: "No recent app data",
          tooltip:
            "Signals from the client’s Vira mobile app—sleep, activity, mood check-ins, and diary cards when enabled. Pulled from their linked app account for this client (most recent 7 days of daily features); used to ground the action plan and progress note. Empty here means no submissions in that window.",
        }
      : {
          id: "app",
          title: "App data",
          status: "disabled",
          detail: "Not required for this draft",
        }
    const resolvedNoteGate = progressNoteGate ?? fetchedNoteGate
    const noteInput: InputStatusItem = (() => {
      if (!loopEnabled) {
        return {
          id: "note",
          title: "Note",
          status: "disabled" as const,
          detail: "Use progress note below",
        }
      }
      if (resolvedNoteGate?.status === "signed") {
        return {
          id: "note",
          title: "Note",
          status: "ready" as const,
          detail: "Finalized",
        }
      }
      if (
        resolvedNoteGate?.status === "draft" ||
        resolvedNoteGate?.hasContent
      ) {
        return {
          id: "note",
          title: "Note",
          status: "in_progress" as const,
          detail: "Draft in progress",
        }
      }
      return {
        id: "note",
        title: "Note",
        status: "missing" as const,
        detail: "Not drafted",
      }
    })()
    return [
      appInput,
      {
        id: "session",
        title: "Session record",
        status: hasTranscriptContent ? "ready" : "missing",
        detail: hasTranscriptContent
          ? `Transcript · ${formatSessionDate(
              transcriptSession!.session.createdAt
            )}`
          : "No recording or transcript yet",
      },
      {
        id: "plan",
        title: "Plan",
        status: planReady
          ? "ready"
          : planInProgress
          ? "in_progress"
          : "missing",
        detail: planDetail,
      },
      noteInput,
    ]
  }, [
    carePlan?.plan,
    fetchedNoteGate,
    hasPlanItems,
    isGenerating,
    isLocked,
    loopEnabled,
    planStatus,
    progressNoteGate,
    statusLabel,
    hasTranscriptContent,
    transcriptSession,
  ])

  const countedInputs = inputs.filter((item) => item.status !== "disabled")
  const readyCount = countedInputs.filter(
    (item) => item.status === "ready"
  ).length
  const totalCount = countedInputs.length
  const onInputsChangeRef = useRef(onInputsChange)
  onInputsChangeRef.current = onInputsChange
  const lastNotifiedInputsRef = useRef<string | null>(null)

  useEffect(() => {
    const fingerprint = JSON.stringify({
      readyCount,
      totalCount,
      items: inputs.map((item) => ({
        id: item.id,
        status: item.status,
        detail: item.detail,
        title: item.title,
      })),
    })
    if (lastNotifiedInputsRef.current === fingerprint) {
      return
    }
    lastNotifiedInputsRef.current = fingerprint
    onInputsChangeRef.current?.({ items: inputs, readyCount, totalCount })
  }, [inputs, readyCount, totalCount])

  if (!enabled) {
    return null
  }

  return (
    <Box
      sx={{
        width: "100%",
        // Match other Treat/Wrap-up panes: no extra top offset under ClientDetailPage.
        mt: loopEnabled || embedded ? 0 : 2,
        mb: embedded ? 2 : 3,
      }}
    >
      <input
        ref={uploadInputRef}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ""
          if (file && onRequestUploadTranscript) {
            onRequestUploadTranscript(file)
          }
        }}
      />
      <PlanAndNotePanel
        variant={panelVariant}
        clientName={selectedClientName}
        showPageHeader={!loopEnabled && !embedded}
        hideNoteCard={embedded}
        showInputs={!hideInputs}
        inputs={inputs}
        readyCount={readyCount}
        totalCount={totalCount}
        planItems={loopPlanItems}
        planStatusLabel={hasPlanItems ? statusLabel : "Not drafted"}
        noteStatus={carePlan?.plan ? "ready" : "locked"}
        notePreview={
          loopEnabled
            ? "Draft from client data, transcript, and plan."
            : "Draft the progress note in the section below."
        }
        theirWeekEntries={theirWeekEntries}
        loopLabels={loopEnabled}
        errorMessage={visibleErrorMessage ?? undefined}
        onApproveItem={(id) => {
          void handleAccept(id)
        }}
        onAddTechnique={
          !isLocked
            ? () => {
                openCreateNudgeModal()
              }
            : undefined
        }
        onCustomizeItem={
          loopEnabled && !isLocked
            ? (id) => {
                openScheduleModal(id)
              }
            : undefined
        }
        onRejectItem={
          isLocked
            ? undefined
            : (id) => {
                void handleReject(id)
              }
        }
        onRemoveItem={
          isLocked
            ? undefined
            : (id) => {
                void handleReject(id)
              }
        }
        onBuildPlanMyself={() => {
          void handleInitializeManualPlan()
        }}
        intro="Build the between-session plan yourself. Auto-generate from the session record when you want a starting draft."
        tabActions={
          isGenerating ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                width: "100%",
              }}
            >
              <LinearProgress sx={{ width: "100%" }} />
              <Typography
                variant="body1"
                sx={{
                  color: "var(--vira-ink, #1E263C)",
                  fontWeight: 600,
                }}
              >
                Generating the action plan…
              </Typography>
            </Box>
          ) : (
            <Tooltip
              title={generatePlanDisabledReason}
              disableHoverListener={!generatePlanDisabledReason}
              disableFocusListener={!generatePlanDisabledReason}
            >
              <span>
                <ViraButton
                  variant="primary"
                  disabled={!canAutoGeneratePlan}
                  onClick={() => {
                    void handleGenerate()
                  }}
                  sx={{ minHeight: 40 }}
                >
                  Auto generate the plan
                </ViraButton>
              </span>
            </Tooltip>
          )
        }
        onUploadTranscript={() => {
          if (onRequestUploadTranscript) {
            uploadInputRef.current?.click()
            return
          }
          setActionErrorMessage(
            embedded
              ? "Use the Transcript tab to upload a file for this session."
              : "Upload a transcript from Sessions, then return here to generate a plan."
          )
        }}
        onRecordRecap={(event?: MouseEvent<HTMLElement>) => {
          if (onRequestRecordRecap && event) {
            onRequestRecordRecap(event)
            return
          }
          setActionErrorMessage(
            embedded
              ? "Use the Transcript tab to record for this session."
              : "Start a session recording from the header, then generate a plan."
          )
        }}
        onDraftNote={() => {
          onDraftNoteRequest?.()
        }}
        planHeaderActions={null}
        footer={
          hasPlanItems ? (
            <Stack spacing={2}>
              {loopEnabled ? (
                <ViraText tone="subtle" sx={{ fontSize: 12.5 }}>
                  After sign, Vira composes patient nudges and creates schedules
                  for delivery using each item&apos;s one-time or weekly
                  settings.
                </ViraText>
              ) : null}

              {!loopEnabled && planItems.length > 0 && !isLocked ? (
                <Stack spacing={1.5}>
                  <ViraText tone="muted" sx={{ fontSize: 12.5 }}>
                    Edit goals before signing (required for approved items).
                  </ViraText>
                  {planItems.map((item) => {
                    const decision = resolveItemDecision(
                      item,
                      carePlan?.approvedItems
                    )
                    if (decision === "rejected") {
                      return null
                    }
                    const goalValue = draftGoals[item.id] ?? item.goal ?? ""
                    const goalSaveError = goalSaveErrors[item.id]
                    const isBusy = busyItemId === item.id
                    const showContingencies =
                      (decision === "approved" ||
                        decision === "edited" ||
                        decision === "signed") &&
                      item.contingencies.length > 0
                    return (
                      <Stack key={`item-edit-${item.id}`} spacing={1}>
                        <ViraTextField
                          label={`Goal · ${item.title || item.id}`}
                          value={goalValue}
                          disabled={isBusy}
                          error={Boolean(goalSaveError) || !goalValue.trim()}
                          helperText={
                            goalSaveError ||
                            (!goalValue.trim()
                              ? "Goal text is missing"
                              : undefined)
                          }
                          onChange={(event) => {
                            const value = event.target.value
                            setDraftGoals((current) => ({
                              ...current,
                              [item.id]: value,
                            }))
                            if (goalSaveErrors[item.id]) {
                              setGoalSaveErrors((current) => {
                                const next = { ...current }
                                delete next[item.id]
                                return next
                              })
                            }
                          }}
                          onBlur={() => {
                            void handleSaveGoal(item)
                          }}
                          fullWidth
                          multiline
                          minRows={2}
                        />
                        {showContingencies ? (
                          <Stack spacing={1} sx={{ pl: 0.5 }}>
                            <ViraText tone="muted" sx={{ fontSize: 12.5 }}>
                              Contingencies · {item.title || item.id}
                            </ViraText>
                            {item.contingencies.map((contingency) => (
                              <ContingencyReviewRow
                                key={contingency.id}
                                contingency={contingency}
                                disabled={isBusy || isSigning}
                                onApprove={() => {
                                  void handleContingencyDecision(
                                    item,
                                    contingency.id,
                                    true
                                  )
                                }}
                                onDecline={() => {
                                  void handleContingencyDecision(
                                    item,
                                    contingency.id,
                                    false
                                  )
                                }}
                              />
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                    )
                  })}
                </Stack>
              ) : null}

              {carePlan?.compositionNote ? (
                <ViraText tone="subtle" sx={{ fontSize: 12.5 }}>
                  {carePlan.compositionNote}
                </ViraText>
              ) : null}

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <ViraButton
                  variant="primary"
                  disabled={
                    isLocked ||
                    isSigning ||
                    approvedCount === 0 ||
                    hasEmptyApprovedGoals
                  }
                  onClick={() => {
                    void handleSign()
                  }}
                  sx={{ minHeight: 40 }}
                >
                  {isSigning
                    ? "Signing…"
                    : loopEnabled
                    ? `Sign and schedule nudges (${approvedCount})`
                    : `Sign approved items (${approvedCount})`}
                </ViraButton>
                {hasEmptyApprovedGoals && !isLocked ? (
                  <ViraText tone="danger" sx={{ fontSize: 12.5 }}>
                    {loopEnabled
                      ? "Added items need goal text before you can sign. Use Customize."
                      : "Approved items need goal text before you can sign."}
                  </ViraText>
                ) : null}
                {!hasEmptyApprovedGoals &&
                pendingContingencyCount > 0 &&
                !isLocked ? (
                  <ViraText tone="subtle" sx={{ fontSize: 12.5 }}>
                    {pendingContingencyCount === 1
                      ? "1 contingency still needs review (signing is allowed)."
                      : `${pendingContingencyCount} contingencies still need review (signing is allowed).`}
                  </ViraText>
                ) : null}
              </Stack>
            </Stack>
          ) : null
        }
      />
      {scheduleDraft && scheduleModalItemId ? (
        <SetTechniqueModal
          open
          mode={scheduleDraft.mode}
          onModeChange={(mode: TechniqueScheduleMode) => {
            setScheduleDraft((current) =>
              current ? { ...current, mode } : current
            )
          }}
          itemType="nudge"
          selectedDays={(scheduleDraft.days ?? []) as DayOfWeek[]}
          onDaysChange={(days) => {
            setScheduleDraft((current) =>
              current
                ? { ...current, days: days as ClinicianScheduleDay[] }
                : current
            )
          }}
          notificationDate={scheduleDraft.date ?? ""}
          onNotificationDateChange={(value) => {
            setScheduleDraft((current) => {
              if (!current) {
                return current
              }
              const next = { ...current, date: value }
              const preset = current.recurrence?.preset ?? "none"
              if (preset === "weekly") {
                return applyRecurrencePreset(next, "weekly")
              }
              return next
            })
          }}
          notificationTime={scheduleDraft.time}
          onNotificationTimeChange={(value) => {
            setScheduleDraft((current) =>
              current ? { ...current, time: value } : current
            )
          }}
          recurrence={
            scheduleDraft.recurrence ?? {
              preset: "none",
              interval: 1,
              unit: "week",
              ends: { type: "never" },
            }
          }
          onRecurrenceChange={(recurrence) => {
            setScheduleDraft((current) => {
              if (!current) {
                return current
              }
              return applyRecurrencePreset(
                {
                  ...current,
                  recurrence: {
                    ...recurrence,
                    ends: recurrence.ends,
                  },
                },
                recurrence.preset as RecurrencePreset,
                recurrence.preset === "custom" ? current.days : undefined
              )
            })
          }}
          recurrenceOptions={recurrenceSelectOptions(scheduleDraft.date)}
          recurrenceSummary={formatRecurrenceSummary(scheduleDraft)}
          notifyScope={(scheduleDraft.notifyScope ?? "only_me") as NotifyScope}
          onNotifyScopeChange={(scope) => {
            setScheduleDraft((current) =>
              current ? { ...current, notifyScope: scope } : current
            )
          }}
          expirationDays={scheduleDraft.expiration?.days ?? "0"}
          expirationHours={scheduleDraft.expiration?.hours ?? "8"}
          expirationMinutes={scheduleDraft.expiration?.minutes ?? "0"}
          onExpirationChange={(part, value) => {
            setScheduleDraft((current) => {
              if (!current) {
                return current
              }
              const expiration = {
                days: current.expiration?.days ?? "0",
                hours: current.expiration?.hours ?? "8",
                minutes: current.expiration?.minutes ?? "0",
                [part]: value,
              }
              return { ...current, expiration }
            })
          }}
          itemTitle={scheduleDraftTitle}
          onItemTitleChange={setScheduleDraftTitle}
          goal={scheduleDraftGoal}
          onGoalChange={(value) => {
            setScheduleDraftGoal(value)
            if (scheduleModalItemId && goalSaveErrors[scheduleModalItemId]) {
              setGoalSaveErrors((current) => {
                const next = { ...current }
                delete next[scheduleModalItemId]
                return next
              })
            }
          }}
          goalError={
            scheduleModalItemId
              ? goalSaveErrors[scheduleModalItemId]
              : undefined
          }
          onClose={
            isSavingSchedule
              ? undefined
              : () => {
                  closeScheduleModal()
                }
          }
          onSave={() => {
            if (!isSavingSchedule) {
              void handleSaveSchedule()
            }
          }}
          showRiskWarning={false}
        />
      ) : null}
    </Box>
  )
}

function resolvePanelVariant(args: {
  hasSession: boolean
  planStatus: string | null
  isGenerating: boolean
  hasPlan: boolean
  itemCount: number
}): PlanAndNoteVariant {
  // Never show the empty-hero card when a session is open — same intro →
  // actions → content chrome as Transcript / Progress note.
  if (!args.hasSession) {
    return "manually_create"
  }
  // Empty or in-progress shell: keep plan card + Add a nudge available.
  if (!args.hasPlan && !args.isGenerating) {
    return "action_plan_drafted_not_approved"
  }
  if (args.isGenerating) {
    return "data_transcript_plan_in_progress"
  }
  if (
    args.planStatus === "composed" ||
    args.planStatus === "composing" ||
    args.planStatus === "composition_failed" ||
    args.planStatus === "signed"
  ) {
    return "data_transcript_plan_complete"
  }
  return "action_plan_drafted_not_approved"
}

/** Best-effort Figma section mapping until API taxonomy is richer. */
function resolvePlanItemSection(item: CarePlanItem): PlanItemSection {
  const haystack = [item.title, item.interventionId, item.rationale, item.goal]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
  if (/\b(phq|gad|assessment|measure|scale|questionnaire)\b/.test(haystack)) {
    return "due_now"
  }
  if (
    /\b(suggest|consider|optional|vira)\b/.test(haystack) ||
    !item.goal?.trim()
  ) {
    return "vira_suggests"
  }
  return "from_session"
}

function truncateLabel(value: string, max = 22): string {
  const trimmed = value.trim()
  if (trimmed.length <= max) {
    return trimmed
  }
  return `${trimmed.slice(0, max - 1)}…`
}

function ContingencyReviewRow({
  contingency,
  disabled,
  onApprove,
  onDecline,
}: {
  contingency: CarePlanContingency
  disabled: boolean
  onApprove: () => void
  onDecline: () => void
}) {
  const ifText = contingency.if?.trim() || "If condition not set"
  const thenText = contingency.then?.trim() || "Then action not set"
  return (
    <Stack
      spacing={0.75}
      sx={{
        borderLeft: "2px solid",
        borderColor: contingency.approved ? "success.main" : "divider",
        pl: 1.25,
        py: 0.5,
      }}
    >
      <ViraText sx={{ fontSize: 12.5 }}>If: {ifText}</ViraText>
      <ViraText sx={{ fontSize: 12.5 }}>Then: {thenText}</ViraText>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        flexWrap="wrap"
        useFlexGap
      >
        <ViraButton
          variant={contingency.approved ? "primary" : "secondary"}
          disabled={disabled}
          onClick={onApprove}
          sx={{ minHeight: 32, px: 1.5 }}
          aria-label={`Approve contingency: ${ifText}`}
        >
          Approve
        </ViraButton>
        <ViraButton
          variant="secondary"
          disabled={disabled}
          onClick={onDecline}
          sx={{ minHeight: 32, px: 1.5 }}
          aria-label={`Decline contingency: ${ifText}`}
        >
          Decline
        </ViraButton>
        {contingency.requiresApproval && !contingency.approved ? (
          <ViraText tone="subtle" sx={{ fontSize: 11.5 }}>
            Needs review
          </ViraText>
        ) : null}
      </Stack>
    </Stack>
  )
}

function buildGoalDrafts(items: CarePlanItem[]): Record<string, string> {
  return Object.fromEntries(items.map((item) => [item.id, item.goal ?? ""]))
}

function resolveItemDecision(
  item: CarePlanItem,
  approvedItems: CarePlanResponse["approvedItems"] | undefined
): string {
  if (item.status === "signed") {
    return "signed"
  }
  return approvedItems?.[item.id] ?? item.status ?? "draft"
}

function formatStatusLabel(
  status: string | null,
  carePlan: CarePlanResponse | null,
  loopEnabled = false
): string {
  if (!status) {
    return "No draft yet"
  }
  if (status === "composing") {
    return loopEnabled ? "Signed · composing nudges" : "Signed · composing"
  }
  if (status === "composed") {
    return loopEnabled
      ? "Signed · nudges scheduled"
      : "Signed · composition ready"
  }
  if (status === "composition_failed") {
    return "Signed · composition failed"
  }
  const knowledgeVersion = carePlan?.plan?.knowledgeVersion?.trim()
  if (
    knowledgeVersion &&
    knowledgeVersion.toLowerCase() !== "unknown" &&
    !knowledgeVersion.toLowerCase().startsWith("draft-unknown")
  ) {
    return `${status} · ${knowledgeVersion}`
  }
  return status
}

function formatSessionDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

/** True when the session has usable transcript text (not an empty manual shell). */
function transcriptDetailHasContent(
  detail: TranscriptSessionDetail | null | undefined
): boolean {
  if (!detail?.transcript) {
    return false
  }
  if (detail.transcript.rawText?.trim()) {
    return true
  }
  return detail.transcript.segments.some((segment) =>
    Boolean(segment.text?.trim())
  )
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0
    ? error.message
    : fallback
}

function formatGoalSaveError(error: unknown): string {
  const message = getErrorMessage(
    error,
    "Unable to save edits for this plan item."
  )
  if (/^not found$/i.test(message.trim())) {
    return "Could not save this goal (endpoint not found). Rebuild/restart transcript-api, then try again."
  }
  if (/plan item not found/i.test(message)) {
    return "Could not save this goal because the plan item is missing from the draft. Try regenerating the care plan."
  }
  return message
}
